/**
 * DocViewSchema for Stock Entry DocType
 * 
 * Schema-driven layout for Stock Entry detail pages.
 * 
 * Tab structure:
 * - Details: Stock entry overview, details, items, and status
 * - Meta: System/audit fields
 * 
 * This schema replaces the legacy FormViewConfig approach.
 * All layout changes should be made in this file.
 */

import type { DocViewSchema } from "./types";

export const stockEntryDocViewSchema: DocViewSchema = {
  doctype: "Stock Entry",
  views: {
    experience: {
      style: "shopify",
      
      // All sections available in this view
      sections: [
        // ===== Details Tab Sections =====
        {
          id: "stock-entry-overview",
          label: "Stock Entry Overview",
          description: "Stock Entry identification and key status information",
          layout: "two-column",
          fields: [
            "name",
            "docstatus",
            "posting_date",
            "posting_time",
          ],
        },
        {
          id: "stock-entry-details",
          label: "Stock Entry Details",
          description: "Core stock entry information",
          layout: "two-column",
          fields: [
            "stock_entry_type",
            "from_warehouse",
            "to_warehouse",
            "company",
            "remarks",
          ],
        },
        {
          id: "items",
          label: "Items",
          description: "Items being moved in this stock entry",
          layout: "one-column",
          fields: [
            "items", // Child table (Stock Entry Detail)
          ],
        },
        {
          id: "status-summary",
          label: "Status Summary",
          description: "Stock Entry value summary (outgoing, incoming, difference)",
          layout: "two-column",
          fields: [
            "total_outgoing_value",
            "total_incoming_value",
            "value_difference",
          ],
        },
        {
          id: "stock-entry-timeline",
          label: "Timeline",
          description: "Creation, posting, and modification history",
          layout: "one-column",
          // Widget reads creation/modified/owner/modified_by/docstatus directly from
          // formData. posting_date is used only to satisfy the schema's real-field
          // requirement so the section survives the conversion filter.
          fields: [
            "posting_date",
          ],
        },
        {
          id: "stock-entry-actions",
          label: "Actions",
          description: "Lifecycle actions for this Stock Entry",
          layout: "one-column",
          // Widget reads docstatus/onSave/onSubmit/isSaving/isNew from props.
          // stock_entry_type is referenced only to survive the conversion filter.
          fields: [
            "stock_entry_type",
          ],
        },

        // ===== Meta Tab Sections =====
        {
          id: "audit",
          label: "System Information",
          description: "Audit and system fields",
          layout: "two-column",
          fields: [
            "owner",
            "creation",
            "modified_by",
            "modified",
          ],
        },
      ],
      
      // Tab definitions - organize sections into navigation
      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: [
            "stock-entry-overview",
            "stock-entry-details",
            "items",
            "status-summary",
            "stock-entry-timeline",
            "stock-entry-actions",
          ],
        },
        {
          id: "meta",
          label: "Meta",
          sectionIds: ["audit"],
        },
      ],
      
      // Quick actions available in header/toolbar
      // TODO: Implement these actions via Doc Actions DSL
      quickActions: [
        // "submit_stock_entry",
        // "cancel_stock_entry",
        // "duplicate_stock_entry",
      ],
      
      // Connection groups for Connections tab
      connectionGroups: [
        "stock",
        "inventory",
      ],
    },
    
    // Create view: guided wizard for new stock entries
    create: {
      style: "shopify",
      sections: [
        {
          id: "stock-entry-type-picker",
          label: "Stock Entry Type",
          description: "Select the type of stock movement",
          widgetId: "StockEntryTypePickerWidget",
        },
        {
          id: "stock-entry-warehouse",
          label: "Warehouse & Details",
          description: "Source/target warehouse, date, and remarks",
          widgetId: "StockEntryWarehouseWidget",
        },
        {
          id: "stock-entry-items",
          label: "Items",
          description: "Products to include in this entry",
          widgetId: "StockEntryItemsWidget",
        },
      ],
      // No tabs — single scrollable wizard flow
    },

    // System view: raw/admin layout, field-focused
    system: {
      sections: [
        {
          id: "basic",
          label: "Basic",
          layout: "two-column",
          fields: [
            "name",
            "docstatus",
            "posting_date",
            "posting_time",
            "stock_entry_type",
            "company",
            "remarks",
          ],
        },
        {
          id: "purpose",
          label: "Purpose & Warehouses",
          layout: "two-column",
          fields: [
            "from_warehouse",
            "to_warehouse",
            "purpose",
            "add_to_transit",
            "in_transit",
            "source_warehouse_address",
            "target_warehouse_address",
          ],
        },
        {
          id: "items",
          label: "Items",
          layout: "one-column",
          fields: [
            "items", // Child table
          ],
        },
        {
          id: "accounting",
          label: "Accounting",
          layout: "two-column",
          fields: [
            "expense_account",
            "cost_center",
            "project",
            "total_outgoing_value",
            "total_incoming_value",
            "total_additional_costs",
            "total_value",
          ],
        },
        {
          id: "audit",
          label: "System Information",
          layout: "two-column",
          fields: [
            "owner",
            "creation",
            "modified_by",
            "modified",
          ],
        },
      ],
      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: ["basic", "purpose", "items", "accounting"],
        },
        {
          id: "meta",
          label: "Meta",
          sectionIds: ["audit"],
        },
      ],
    },
  },
};

